import * as React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
import { useDispatch, useSelector } from 'react-redux'
import { change, FieldArray, getFormValues, reduxForm } from 'redux-form'
// components
import { Button, BUTTON_LAYOUTS, DrawerModal } from 'components/UI'
import { CloseIcon } from 'components/UI/Icons'
// view components
import CustomColumns from './components/CustomColumns'
// views
import { ButtonsWrapper, Container, Header } from './views'
// constants
import { Forms, Modals } from 'constants/index'
// graphql
import { PLAN_UPDATE_MUTATION } from 'graphql/plan/mutations'
// context
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// hooks
import { useModalData } from 'hooks/index'
// types
import { IPlanColumn } from 'types/plan/columns'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { parseColumnSettings, toColumnSettings } from 'utils/planColumns'

const PAPER_PROPS = { style: { overflow: 'hidden' } }

const PlanColumnsSettingsModal: React.FC = () => {
  const dispatch = useDispatch()

  const { plan } = React.useContext(PlanEditPageContext)

  const appliedColumns = React.useMemo(() => parseColumnSettings(plan?.columnSettings), [plan?.columnSettings])

  // Read the columns straight from the form: Save must work even while a column draft is open,
  // so it never goes through handleSubmit (a draft's `required` select would block it).
  const formValues = useSelector(getFormValues(Forms.PLAN_SETTINGS_FORM)) as { columns?: IPlanColumn[] }

  const {
    hideModal,
    modalData: { isOpen, isDisabled }
  } = useModalData({ modalType: Modals.PLAN_COLUMNS_SETTINGS_MODAL })

  const [updatePlan, { loading: isSaving }] = useMutation(PLAN_UPDATE_MUTATION, {
    update: (cache, _result, { variables }) => {
      if (!variables) {
        return
      }

      cache.modify({
        id: cache.identify({ __typename: 'Plan', id: variables.id }),
        fields: { columnSettings: () => variables.input.columnSettings }
      })
    }
  })

  React.useEffect(() => {
    if (isOpen) {
      dispatch(change(Forms.PLAN_SETTINGS_FORM, 'columns', appliedColumns))
      dispatch(change(Forms.PLAN_SETTINGS_FORM, 'fields', []))
    }
  }, [isOpen])

  const handleSave = async () => {
    const columnSettings = toColumnSettings(formValues?.columns ?? appliedColumns)

    try {
      await updatePlan({ variables: { id: plan.id, input: { columnSettings } } })
      hideModal()
    } catch (error) {
      console.log('[PlanColumnsSettingsModal.handleSave]', parseGraphQLErrorMessages(error))
    }
  }

  return (
    <DrawerModal width='400px' isOpen={isOpen} hideModal={hideModal} PaperProps={PAPER_PROPS}>
      <Header>
        <span>Columns Settings</span>
        <CloseIcon onClick={hideModal} />
      </Header>
      <Container>
        <FieldArray<any> name='fields' component={CustomColumns} isDisabled={!!isDisabled} />

        <ButtonsWrapper>
          <Button type={'button'} layout={BUTTON_LAYOUTS.PURPLE} shadowLess isLoading={isSaving} onClick={handleSave}>
            Save
          </Button>
          <Button type={'button'} layout={BUTTON_LAYOUTS.PURPLE_INVERTED} shadowLess onClick={hideModal}>
            Cancel
          </Button>
        </ButtonsWrapper>
      </Container>
    </DrawerModal>
  )
}

export default reduxForm<any, any>({
  form: Forms.PLAN_SETTINGS_FORM,
  destroyOnUnmount: false
})(PlanColumnsSettingsModal)
